import { FileExclamationOutlined, FileDoneOutlined, WarningOutlined } from '@ant-design/icons';
import { Badge, Button, Select, Tabs } from 'antd';
import { useFMCSA, useFmcsaCount, useFmcsaFailedList } from '../../../../Hooks/TT_ELD/FMCSA';
import { useState } from 'react';
import FMCSA_Table from './FMCSA_Table/FMCSA_Table';
import useRouteQuery from '../../../../Utils/useRouterQuery';
import { useTagQuerySync } from '../../../../Utils/useTagQuerySync';
import FmcsaFailedFromApp from './FMCSA_Table/FMCSA_FailedFromApp';

const { Option } = Select;

const report_status = [
  { label: 'Needs review', value: 'needs_review' },
  { label: 'Checking', value: 'checking' },
];

const FMCSAReviewTab = ({
  status,
  skip,
  setStatus,
  setSkip,
  data,
  refetch,
  isLoading,
  isFetching,
}: any) => {
  const count = useFmcsaCount();

  const onChange = (query: any) => {
    setSkip(query.current);
  };

  const handleChange = (value: string) => {
    setStatus(value);
    setSkip(1);
  };

  const refresh = () => {
    refetch();
    count.refetch();
  };

  return (
    <div>
      <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Select
          value={status || 'all'}
          onChange={(value) => handleChange(value === 'all' ? '' : value)}
          style={{ width: 200 }}
        >
          <Option value="all">All Status</Option>
          {report_status.map((u) => (
            <Option key={u.value} value={u.value}>
              {u.label}
            </Option>
          ))}
        </Select>
        <Button size="large" onClick={refresh}>
          Refresh
        </Button>
      </span>
      <FMCSA_Table
        data={data?.data}
        onChange={onChange}
        total={data?.count}
        isLoading={isLoading}
        isFetching={isFetching}
        refetch={refresh}
        status="needs_review"
        skip={skip}
      />
    </div>
  );
};

const FmcsacheckTab = ({ skip, setSkip, refetch, data, isLoading, isFetching }: any) => {
  const count = useFmcsaCount();

  const onChange = (query: any) => {
    setSkip(query.current);
  };

  const refresh = () => {
    refetch();
    count.refetch();
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
        <Button style={{ marginBottom: 6 }} size="large" onClick={refresh}>
          Refresh
        </Button>
      </div>
      <FMCSA_Table
        data={data?.data}
        onChange={onChange}
        total={data?.count}
        isLoading={isLoading}
        isFetching={isFetching}
        refetch={refresh}
        status="checking"
        skip={skip}
      />
    </div>
  );
};

const FmcsaFailedFromAppTab = ({ skip, setSkip, refetch, data, isLoading, isFetching }: any) => {
  const onChange = (query: any) => {
    setSkip(query.current);
  };

  return (
    <FmcsaFailedFromApp
      data={data?.data}
      onChange={onChange}
      total={data?.count}
      isLoading={isLoading}
      isFetching={isFetching}
      refetch={refetch}
      skip={skip}
    />
  );
};

const Fmcsa = () => {
  const getUserRole = () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      return user?.role || '';
    } catch {
      return '';
    }
  };

  const canViewFailedFromApp = ['support', 'support_manager', 'developer', 'manager'].includes(
    getUserRole(),
  );

  const [tabKey, setTabKey] = useRouteQuery('1', 'tabKey', (v) => String(v));
  const [status, setStatus] = useRouteQuery('', 'status', (v) => String(v));
  const [skip, setSkip] = useState<number>(1);
  const [failedSkip, setFailedSkip] = useState<number>(1);

  const { data, refetch, isLoading, isFetching } = useFMCSA(status, skip);

  const {
    data: checkData,
    refetch: checkRefetch,
    isLoading: checkIsLoading,
    isFetching: checkIsFetching,
  } = useFMCSA('checked', skip);

  const {
    data: failedData,
    refetch: failedRefetch,
    isLoading: failedIsLoading,
    isFetching: failedIsFetching,
  } = useFmcsaFailedList(failedSkip);

  useTagQuerySync(
    { en_US: 'FMCSA', zh_CN: '' },
    'fmcsa',
    { tabKey, status, skip },
    { tabKey: setTabKey, status: setStatus, skip: setSkip },
  );

  const items = [
    {
      label: (
        <span>
          <FileExclamationOutlined style={{ marginRight: 6 }} />
          Unchecked{' '}
          <Badge
            count={data?.uncheckedCount}
            size="default"
            className="blue-badge"
            style={{ marginLeft: 8 }}
          />
        </span>
      ),
      key: '1',
      children: (
        <FMCSAReviewTab
          status={status}
          skip={skip}
          setStatus={setStatus}
          setSkip={setSkip}
          data={data}
          refetch={refetch}
          isLoading={isLoading}
          isFetching={isFetching}
        />
      ),
    },
    {
      label: (
        <span>
          <FileDoneOutlined style={{ marginRight: 6 }} />
          Checked{' '}
          <Badge
            count={checkData?.checkedCount}
            size="default"
            className="blue-badge"
            style={{ marginLeft: 8 }}
          />
        </span>
      ),
      key: '2',
      children: (
        <FmcsacheckTab
          skip={skip}
          setSkip={setSkip}
          data={checkData}
          refetch={checkRefetch}
          isLoading={checkIsLoading}
          isFetching={checkIsFetching}
        />
      ),
    },
    ...(canViewFailedFromApp
      ? [
          {
            label: (
              <span>
                <WarningOutlined style={{ marginRight: 6 }} />
                Failed From App{' '}
                <Badge
                  count={failedData?.count}
                  size="default"
                  className="blue-badge"
                  style={{ marginLeft: 8 }}
                />
              </span>
            ),
            key: '3',
            children: (
              <FmcsaFailedFromAppTab
                skip={failedSkip}
                setSkip={setFailedSkip}
                data={failedData}
                refetch={failedRefetch}
                isLoading={failedIsLoading}
                isFetching={failedIsFetching}
              />
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="mainPage">
      <Tabs activeKey={tabKey} onTabClick={setTabKey} items={items} />
    </div>
  );
};

export default Fmcsa;
